import { type FC, type ReactNode, useState } from 'react'
import { ActivityStageToLabelTKey } from '@/features/luxe-submissions/components/listing-activity-entry'
import { useLuxeFeatureFlag } from '@/features/luxe-submissions/queries/use-get-luxe-feature-flags'
import type { SubmissionActivityEntry } from '@/features/luxe-submissions/types'
import { getScoreTextColor } from '@/features/luxe-submissions/utils'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

interface PinnedEscalationNoteProps {
	note?: string | null
	// The activity entry the note was submitted with; the stage comes from it
	details?: SubmissionActivityEntry
	// The breakdown of the score badge in the page header, which carries no
	// tooltip of its own there. Gated by luxe-vv2-final-score-display.
	designScore?: number | null
	blockersFailedCount?: number | null
	// Fallback for pages whose activity entry is missing
	reviewerName?: string | null
	// The MM's note from requesting a second review, pinned in its own card
	// under the escalation note
	secondReviewNote?: string | null
	secondReviewDetails?: SubmissionActivityEntry
}

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
	<div className='flex items-center justify-between gap-4'>
		<dt className='font-medium text-neutral-500 text-sm/5'>{label}</dt>
		<dd className='font-medium text-neutral-900'>{value}</dd>
	</div>
)

const DetailRows = ({
	designScore,
	blockersFailedCount,
	reviewer,
	stageEntry,
	hasNote
}: {
	designScore?: number | null
	blockersFailedCount?: number | null
	reviewer?: string | null
	// The activity entry whose stage is shown; the stage goes with its note
	stageEntry?: SubmissionActivityEntry
	hasNote: boolean
}) => {
	const { t } = useTranslation()

	return (
		<dl className={cn('flex flex-col gap-2 text-sm', hasNote && 'mt-4')}>
			{designScore != null && (
				<>
					<DetailRow
						label={t('luxe_submissions:final_score_design_label')}
						value={
							<span className={getScoreTextColor(designScore)}>
								{Math.floor(designScore)}
							</span>
						}
					/>
					<DetailRow
						label={t('luxe_submissions:final_score_blockers_label')}
						value={blockersFailedCount ?? 0}
					/>
				</>
			)}

			{reviewer && (
				<DetailRow
					label={t('luxe_submissions:reviewer_column')}
					value={reviewer}
				/>
			)}

			{stageEntry && (
				<DetailRow
					label={t('luxe_submissions:note_stage_label')}
					value={t(ActivityStageToLabelTKey[stageEntry.stage])}
				/>
			)}
		</dl>
	)
}

// A note with its details folded behind a toggle
const NoteCard = ({
	title,
	text,
	details
}: {
	title: string
	text: string
	details?: ReactNode
}) => {
	const { t } = useTranslation()
	const [isExpanded, setIsExpanded] = useState(false)

	return (
		<section className='flex shrink-0 flex-col gap-2 rounded-2xl border border-grey-400 bg-neutral-white p-4 shadow-xs'>
			<h3 className='font-semibold text-base text-neutral-900'>
				{title}
			</h3>

			<p className='wrap-break-word max-h-40 overflow-y-auto whitespace-pre-wrap text-neutral-600 text-sm'>
				{text}
			</p>

			{details && (
				<>
					<button
						className='w-fit cursor-pointer font-semibold text-neutral-900 text-sm'
						onClick={() => setIsExpanded((expanded) => !expanded)}
						type='button'
					>
						{t(
							isExpanded
								? 'luxe_submissions:hide_details'
								: 'luxe_submissions:show_details_toggle'
						)}
					</button>

					{isExpanded && details}
				</>
			)}
		</section>
	)
}

/**
 * The note the reviewer had to leave when escalating the listing from the
 * scorecard, pinned to the top of the scorecard column so it stays visible
 * while the questions scroll underneath it. Must be rendered as the first child
 * of the scorecard's scroll container for `sticky` to take effect.
 * A long note scrolls inside the card rather than eating the column.
 * Without a note the card still pins the score breakdown and the reviewer,
 * already expanded since there is nothing above them to toggle from.
 * The MM's second review note gets a card of its own below, with its reviewer
 * and stage but no score.
 */
export const PinnedEscalationNote: FC<PinnedEscalationNoteProps> = ({
	note,
	details,
	designScore,
	blockersFailedCount,
	reviewerName,
	secondReviewNote,
	secondReviewDetails
}) => {
	const { t } = useTranslation()
	const { isEnabled: isFinalScoreEnabled } = useLuxeFeatureFlag(
		'luxe-vv2-final-score-display'
	)

	const hasEscalationNote = !!note
	// With the flag off the header badge already shows the design score
	const hasScoreBreakdown = isFinalScoreEnabled && designScore != null
	const reviewer = details?.reviewerName ?? reviewerName
	const hasEscalationDetails =
		hasScoreBreakdown || !!reviewer || (hasEscalationNote && !!details)
	const secondReviewer = secondReviewDetails?.reviewerName

	// The reviewer alone isn't worth pinning a card over the questions for
	if (!(hasEscalationNote || hasScoreBreakdown || secondReviewNote)) {
		return null
	}

	const escalationRows = (
		<DetailRows
			blockersFailedCount={blockersFailedCount}
			designScore={hasScoreBreakdown ? designScore : null}
			hasNote={hasEscalationNote}
			reviewer={reviewer}
			stageEntry={hasEscalationNote ? details : undefined}
		/>
	)

	return (
		// Matches the ScoreFormPanel side padding; the white backdrop hides the
		// questions as they scroll under the cards
		<div className='sticky top-0 z-10 flex flex-col gap-4 bg-neutral-white p-6 pl-8'>
			{hasEscalationNote && (
				<NoteCard
					details={hasEscalationDetails ? escalationRows : undefined}
					text={note}
					title={t('luxe_submissions:pinned_escalation_note_title')}
				/>
			)}

			{!hasEscalationNote && hasScoreBreakdown && (
				<section className='flex shrink-0 flex-col gap-2 rounded-2xl border border-grey-400 bg-neutral-white p-4 shadow-xs'>
					<h3 className='font-semibold text-base text-neutral-900'>
						{t('luxe_submissions:pinned_summary_title')}
					</h3>

					{escalationRows}
				</section>
			)}

			{secondReviewNote && (
				<NoteCard
					details={
						secondReviewer ? (
							<DetailRows
								hasNote
								reviewer={secondReviewer}
								stageEntry={secondReviewDetails}
							/>
						) : undefined
					}
					text={secondReviewNote}
					title={t('luxe_submissions:pinned_mm_note_title')}
				/>
			)}
		</div>
	)
}
